import React, { useEffect, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";
import { seoApi } from "@/lib/seoApi";

function Table({ rows, cols, testId }) {
  if (!rows?.length) return <div className="text-sm text-neutral-500 py-4">No data yet.</div>;
  return (
    <table className="w-full text-sm" data-testid={testId}>
      <thead>
        <tr className="text-left text-neutral-500 border-b">
          {cols.map((c) => <th key={c.key} className={`py-2 ${c.right ? "text-right" : ""}`}>{c.label}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-b border-neutral-100">
            {cols.map((c) => (
              <td key={c.key} className={`py-2 ${c.right ? "text-right font-semibold" : ""}`}>
                {c.render ? c.render(r) : r[c.key]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const PERIODS = [
  { value: 7, label: "7 days" },
  { value: 30, label: "30 days" },
  { value: 90, label: "90 days" },
  { value: null, label: "All time" },
];

export default function Leads() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState(null);

  useEffect(() => {
    setData(null);
    seoApi.get("/seo/leads/analytics", { params: period ? { days: period } : {} })
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || e.message));
  }, [period]);

  if (error) return <div className="p-6 text-red-600" data-testid="leads-error">{String(error)}</div>;

  return (
    <div className="p-6 space-y-6" data-testid="leads-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900">Leads</h1>
          <p className="text-sm text-neutral-500 mt-1">Enquiry attribution across blog articles, services and verticals.</p>
        </div>
        <div className="flex gap-1 rounded-lg border border-neutral-200 p-1 bg-white" data-testid="leads-period-filter">
          {PERIODS.map((p) => (
            <button
              key={String(p.value)}
              data-testid={`leads-period-${p.value ?? "all"}`}
              onClick={() => setPeriod(p.value)}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                period === p.value
                  ? "bg-neutral-900 text-white"
                  : "text-neutral-600 hover:bg-neutral-100"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>
      {!data ? <div className="text-neutral-500 py-10">Loading…</div> : <>

      <Card>
        <CardHeader><CardTitle className="text-base">Weekly trend (last 12 weeks)</CardTitle></CardHeader>
        <CardContent>
          <div className="h-48" data-testid="leads-weekly-trend">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.weekly_trend || []} margin={{ top: 4, right: 8, left: -24, bottom: 0 }}>
                <XAxis dataKey="week" tick={{ fontSize: 10 }} tickFormatter={(w) => w.slice(5)} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v) => [v, "Leads"]} labelFormatter={(w) => `Week of ${w}`} />
                <Bar dataKey="leads" fill="#171717" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
      

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card><CardContent className="pt-6">
          <div className="text-3xl font-semibold" data-testid="leads-total">{data.total_leads}</div>
          <div className="text-sm text-neutral-500">Total leads</div>
        </CardContent></Card>
        <Card><CardContent className="pt-6">
          <div className="text-3xl font-semibold" data-testid="leads-attributed">{data.attributed_leads}</div>
          <div className="text-sm text-neutral-500">With source attribution</div>
        </CardContent></Card>
        <Card><CardContent className="pt-6">
          <div className="text-3xl font-semibold" data-testid="leads-cta-pct">{data.cta_attribution_pct}%</div>
          <div className="text-sm text-neutral-500">CTA attribution share</div>
        </CardContent></Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="text-base">Leads by article</CardTitle></CardHeader>
          <CardContent>
            <Table testId="leads-by-article" rows={data.by_article} cols={[
              { key: "title", label: "Article", render: (r) => (
                <a href={`/blog/${r.slug}`} target="_blank" rel="noreferrer" className="hover:underline">{r.title}</a>) },
              { key: "vertical", label: "Vertical" },
              { key: "leads", label: "Leads", right: true },
            ]} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">Leads by vertical</CardTitle></CardHeader>
          <CardContent>
            <Table testId="leads-by-vertical" rows={data.by_vertical} cols={[
              { key: "vertical", label: "Vertical" },
              { key: "leads", label: "Leads", right: true },
            ]} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">Leads by service (CTA on /services)</CardTitle></CardHeader>
          <CardContent>
            <Table testId="leads-by-service" rows={data.by_service} cols={[
              { key: "slug", label: "Service" },
              { key: "leads", label: "Leads", right: true },
            ]} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">Leads by enquiry topic</CardTitle></CardHeader>
          <CardContent>
            <Table testId="leads-by-topic" rows={data.by_topic} cols={[
              { key: "topic", label: "Topic" },
              { key: "leads", label: "Leads", right: true },
            ]} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">Leads by country</CardTitle></CardHeader>
          <CardContent>
            <Table testId="leads-by-country" rows={data.by_country} cols={[
              { key: "country", label: "Country" },
              { key: "leads", label: "Leads", right: true },
            ]} />
          </CardContent>
        </Card>
      </div>

      <p className="text-xs text-neutral-400">{data.note}</p>
      </>}
    </div>
  );
}
